import { UnitMeasurementDto } from '@/api/generated';
import {
  unitMeasurementOptionsTranslation,
  UnitOptionsTranslation,
} from '@/assets/locales/utils/dictionaries/dictionaries.translation.pl';
import {
  formConditionalContainer,
  FormConditionalContainerType,
  OfferElementTypeOptions,
  offerElementTypeOptions,
} from '@/assets/locales/views/offerElements/offerElements.common.translation.pl.ts';
import { ProcessOfferElementCustomAddItemFormModel } from '@/views/processes/elements/components/custom/components/add/model/ProcessOfferElementCustomAddItemForm.model.ts';

type Form = Record<keyof ProcessOfferElementCustomAddItemFormModel, string>;
const form: Form = {
  calculatedPrice: 'Wyliczona cena',
  description: 'Opis',
  name: 'Nazwa',
  price: 'Cena',
  quantity: 'Ilość',
};

export type ProcessOfferElementsTranslation = {
  comment: {
    button: {
      cancel: string;
      save: string;
    };
    noComment: string;
    title: string;
  };
  customItems: {
    form: Form & {
      button: {
        cancel: string;
        save: string;
      };
    };
    table: {
      head: Form;
    };
    title: string;
    toggleShow: string;
  };
  details: FormConditionalContainerType & {
    button: {
      cancel: string;
      submit: string;
    };
    calculatedPrice: string;
    comment: string;
    description: string;
    manuallyCalculatedPrice: string;
    minimumPrice: string;
    name: string;
    noOfElementsForUnit: Record<UnitMeasurementDto, string>;
    orderedQuantity: string;
    orderedQuantityUnit: Record<UnitMeasurementDto, string>;
    priceResolvedForQuantity: string;
    quantity: string;
    salesmanPrice: string;
    salesmanUnitPrice: string;
    salesmanUnitPriceUnit: Record<UnitMeasurementDto, string>;
    unit: string;
    unitOptions: UnitOptionsTranslation;
    unitPrice: string;
    upToQuantity: string;
    useUserPrice: string;
  };
  elementTypes: OfferElementTypeOptions;
  predefinedItems: {
    button: {
      manage: string;
    };
    dialog: {
      offerElements: {
        button: {
          cancel: string;
          submit: string;
        };
      };
    };
    picker: {
      actions: {
        addChecked: string;
        clear: string;
        clearAvailableTooltip: string;
        clearSelectedTooltip: string;
        finishManaging: string;
        finishManagingTooltip: string;
        removeChecked: string;
        selectAll: string;
        selectAllAvailableTooltip: string;
        selectAllSelectedTooltip: string;
      };
      available: {
        empty: string;
        loading: string;
        title: string;
      };
      filters: {
        activeOnly: string;
        activeOnlyTooltip: string;
        allTooltip: string;
      };
      searchPlaceholder: string;
      selected: {
        empty: string;
        title: string;
      };
      summary: {
        checked: string;
        currency: string;
        selected: string;
      };
    };
    title: string;
  };
};

export const processOfferElementsTranslation: ProcessOfferElementsTranslation = {
  comment: {
    button: {
      cancel: 'Anuluj',
      save: 'Zapisz',
    },
    noComment: 'Brak komentarza',
    title: 'Komentarz - pojawi się na wydruku dla działu realizacji',
  },
  customItems: {
    form: {
      button: {
        cancel: 'Anuluj',
        save: 'Zapisz',
      },
      ...form,
    },
    table: {
      head: form,
    },
    title: 'Elementy dodane ręcznie',
    toggleShow: 'Dodaj element ręcznie',
  },
  details: {
    ...formConditionalContainer,
    button: {
      cancel: 'Anuluj',
      submit: 'Zapisz',
    },
    calculatedPrice: 'Cena wyliczona',
    comment: 'Komentarz - pojawi się na wydruku dla działu realizacji',
    description: 'Opis',
    manuallyCalculatedPrice: 'Cena wyliczona ręcznie',
    minimumPrice: 'Cena minimalna',
    name: 'Nazwa',
    noOfElementsForUnit: {
      [UnitMeasurementDto.Gram]: 'błąd',
      [UnitMeasurementDto.Hour]: 'Zamówienie (liczba osób)',
      [UnitMeasurementDto.Milliliter]: 'błąd',
      [UnitMeasurementDto.People]: 'Zamówienie (liczba sztuk)',
      [UnitMeasurementDto.Unit]: 'Zamówienie (liczba sztuk)',
    },
    orderedQuantity: 'Liczba',
    orderedQuantityUnit: {
      [UnitMeasurementDto.Gram]: 'gramów',
      [UnitMeasurementDto.Hour]: 'godzin',
      [UnitMeasurementDto.Milliliter]: 'mililitrów',
      [UnitMeasurementDto.People]: 'osób',
      [UnitMeasurementDto.Unit]: 'sztuk',
    },
    priceResolvedForQuantity: 'Cena dla',
    quantity: 'Ilość',
    salesmanPrice: 'Cena całkowita',
    salesmanUnitPrice: 'Cena za',
    salesmanUnitPriceUnit: {
      [UnitMeasurementDto.Gram]: 'gram',
      [UnitMeasurementDto.Hour]: 'godzinę',
      [UnitMeasurementDto.Milliliter]: 'mililit',
      [UnitMeasurementDto.People]: 'osobę',
      [UnitMeasurementDto.Unit]: 'sztukę',
    },
    unit: 'Jednostka',
    unitOptions: unitMeasurementOptionsTranslation,
    unitPrice: 'Cena jednostkowa',
    upToQuantity: 'Maksymalnie',
    useUserPrice: 'Cena nadpisana',
  },
  elementTypes: offerElementTypeOptions,
  predefinedItems: {
    button: {
      manage: 'Zarządzaj',
    },
    dialog: {
      offerElements: {
        button: {
          cancel: 'Anuluj',
          submit: 'Zapisz',
        },
      },
    },
    picker: {
      actions: {
        addChecked: 'Dodaj zaznaczone ({{count}})',
        clear: 'Wyczyść',
        clearAvailableTooltip: 'Odznacz zaznaczone pozycje na liście dostępnych',
        clearSelectedTooltip: 'Odznacz zaznaczone pozycje na liście wybranych',
        finishManaging: 'Zakończ zarządzanie',
        finishManagingTooltip:
          'Zmiany zapisują się automatycznie - przycisk czeka na zapis i zamyka panel',
        removeChecked: 'Cofnij zaznaczone ({{count}})',
        selectAll: 'Zaznacz wszystkie',
        selectAllAvailableTooltip:
          'Zaznacz wszystkie pozycje z listy dostępnych (do dodania strzałką »)',
        selectAllSelectedTooltip: 'Zaznacz wszystkie wybrane pozycje (do cofnięcia strzałką «)',
      },
      available: {
        empty: 'Brak pozycji pasujących do filtrów.',
        loading: 'Wczytano {{loadedCount}} z {{totalCount}} pozycji...',
        title: 'Dostępne',
      },
      filters: {
        activeOnly: 'Tylko aktywne',
        activeOnlyTooltip: 'Pokazuję tylko aktywne pozycje. Kliknij aby pokazać też nieaktywne.',
        allTooltip: 'Pokazuję wszystkie pozycje. Kliknij aby ukryć nieaktywne.',
      },
      searchPlaceholder: 'Wyszukaj...',
      selected: {
        empty: 'Nic nie wybrano. Użyj "+" przy pozycji albo zaznacz wiele i wciśnij »',
        title: 'Wybrane',
      },
      summary: {
        checked: '{{selectedCount}}/{{totalCount}} zaznaczone',
        currency: 'zł',
        selected: 'wybranych',
      },
    },
    title: 'Elementy z listy',
  },
};
